/**
 * PaymentCollection — "إنشاء فاتورة" (Create Invoice) hub for one Finance
 * sub-platform.
 *
 * Renders the invoice list for the current platform + a "Create Invoice" CTA
 * that navigates to a dedicated full-page form (InvoiceCreatePage). The
 * platform is inferred from the route (see App.js), so the operator never
 * re-picks it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AnimatePresence } from 'framer-motion';
import {
  Plus, RefreshCw, Loader2, ExternalLink, Copy, Trash2,
  CheckCircle2, XCircle, Clock, AlertTriangle, FileDown, Wallet, Link2, Printer,
} from 'lucide-react';

import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';
import { FINANCE_PLATFORM_META } from '../../constants/financePlatforms';
import AddPaymentModal from './AddPaymentModal';
import { DirhamPrice } from '../../components/DirhamPrice';
import { printReceipt, downloadReceiptPDF } from '../../utils/printReceipt';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  unpaid:         { icon: Clock,         color: 'text-amber-400 bg-amber-500/10 border-amber-500/30',   ar: 'فاتورة غير مدفوعة', en: 'Unpaid invoice' },
  paid:           { icon: CheckCircle2,  color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30', ar: 'مدفوعة',            en: 'Paid' },
  partially_paid: { icon: AlertTriangle, color: 'text-blue-400 bg-blue-500/10 border-blue-500/30',      ar: 'مدفوعة جزئياً',       en: 'Partial' },
  cancelled:      { icon: XCircle,       color: 'text-white/40 bg-white/5 border-white/10',             ar: 'ملغاة',                en: 'Cancelled' },
  refunded:       { icon: RefreshCw,     color: 'text-purple-400 bg-purple-500/10 border-purple-500/30', ar: 'مستردّة',             en: 'Refunded' },
};

const METHOD_LABEL = {
  cash:          { ar: 'نقدي',          en: 'Cash' },
  bank_transfer: { ar: 'تحويل بنكي',    en: 'Bank Transfer' },
  check:         { ar: 'شيك',           en: 'Check' },
  credit_card:   { ar: 'بطاقة ائتمان',  en: 'Credit Card' },
  tamara:        { ar: 'تمارا',         en: 'Tamara' },
  tabby:         { ar: 'تابي',          en: 'Tabby' },
  stripe:        { ar: 'ستريب',         en: 'Stripe' },
  ziina:         { ar: 'زينة',          en: 'Ziina' },
};

export default function PaymentCollection({ platformOverride }) {
  const { language } = useLanguage();
  const { platform: platformParam } = useParams();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  // Resolve platform key (prop > route > default)
  const platform = platformOverride || platformParam || 'cloudgm_customers';
  const platMeta = FINANCE_PLATFORM_META[platform];

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [linkingInvoice, setLinkingInvoice] = useState(null); // iter-220 — convert to payment link

  // Parent-hub route (used for back button + breadcrumb) — differs per platform
  const parentHubPath = useMemo(() => {
    if (platform === 'cloudgm_customers') return '/dashboard/finance/cloudgm/customers';
    if (platform === 'cloudgm_merchants') return '/dashboard/finance/cloudgm/merchants';
    // iter-261 — Agents platform sits under the merchant finance hub.
    if (platform === 'agents')            return '/dashboard/finance/cloudgm/merchants';
    return `/dashboard/finance/platform/${platform}`;
  }, [platform]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Best-effort gateway reconciliation BEFORE fetching invoices — so any
      // Tamara/Ziina/Stripe payments the customer completed since the last
      // page load flip to `paid` in the DB and the list shows fresh status.
      try {
        await axios.post(`${API_URL}/api/admin/invoices/reconcile-pending`,
          {}, { headers: getAuthHeaders(), params: { platform } });
      } catch { /* non-fatal */ }
      const r = await axios.get(`${API_URL}/api/admin/invoices`, {
        headers: getAuthHeaders(),
        params: { platform },
      });
      setInvoices(r.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [platform, isAr]);

  const forceReconcile = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/admin/invoices/reconcile-pending`,
        {}, { headers: getAuthHeaders(), params: { platform } });
      const upd = r.data?.updated_count || 0;
      if (upd > 0) {
        toast.success(t(`✓ تم تحديث ${upd} فاتورة`, `✓ Updated ${upd} invoice(s)`));
      } else {
        toast.info(t('لا تحديثات جديدة', 'No new updates'));
      }
      load();
    } catch {
      toast.error(t('فشل التحديث', 'Refresh failed'));
    }
  };

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const s = { count: invoices.length, paid: 0, unpaid: 0, total_paid: 0, total_unpaid: 0 };
    invoices.forEach(inv => {
      if (inv.status === 'paid') { s.paid += 1; s.total_paid += inv.total || 0; }
      else if (inv.status === 'unpaid') { s.unpaid += 1; s.total_unpaid += inv.total || 0; }
    });
    return s;
  }, [invoices]);

  const copyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success(t('تم النسخ', 'Copied')));
  };

  const deleteInvoice = async (inv, opts = {}) => {
    const force = !!opts.force;
    const label = inv.invoice_no_display || `INV-${inv.invoice_no}`;
    const confirmMsg = force
      ? t(
          `⚠️ حذف نهائي (وضع تجريبي): سيتم حذف الفاتورة المدفوعة ${label} وإلغاء ربطها بأي طلب خدمة. المتابعة؟`,
          `⚠️ FORCE delete (trial mode): the PAID invoice ${label} will be removed and unlinked from any service request. Continue?`,
        )
      : t(`حذف الفاتورة ${label}؟`, `Delete invoice ${label}?`);
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/invoices/${inv.id}`, {
        headers: getAuthHeaders(),
        params: force ? { force: true } : undefined,
      });
      toast.success(t('تم الحذف', 'Deleted'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    }
  };

  const markPaid = async (inv) => {
    try {
      await axios.patch(`${API_URL}/api/admin/invoices/${inv.id}`,
        { status: 'paid' }, { headers: getAuthHeaders() });
      toast.success(t('✓ تم تحديث الحالة', '✓ Status updated'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const downloadPdf = (inv) => {
    // iter-243 (Feb 2026) — Merchant platform invoices download using the
    // UNIFIED receipt template (`downloadReceiptPDF`) so every merchant
    // invoice/receipt shares the same design (CLOUDGM logo + gradient +
    // Cairo font + status stamp). Non-merchant platforms keep the
    // legacy backend PDF for now.
    // iter-245 — Same design for `cloudgm_customers`.
    if (platform === 'cloudgm_merchants' || platform === 'cloudgm_customers') {
      downloadReceiptPDF(_invoiceToReceipt(inv), { language });
      return;
    }
    const tokenValue = getAuthHeaders().Authorization?.replace(/^Bearer /, '') || '';
    const url = `${API_URL}/api/admin/invoices/${inv.id}/pdf?token=${encodeURIComponent(tokenValue)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // iter-242 (Feb 2026) — Merchant platform invoices print with the SAME
  // beautiful receipt design used at "مهام الوكيل → طباعة الإيصال"
  // (`printReceipt`). We map the invoice shape → receipt shape and hand it
  // to the shared template.
  const _invoiceToReceipt = (inv) => {
    const services = inv.services || [];
    const first    = services[0] || {};
    const rest     = services.slice(1);
    const joinedAr = rest.map((s) => s.service_type_ar || s.service_type_en).filter(Boolean).join(' • ');
    const joinedEn = rest.map((s) => s.service_type_en || s.service_type_ar).filter(Boolean).join(' • ');
    const methodMap = {
      cash: 'receipt', bank_transfer: 'bank_transfer', check: 'bank_transfer',
      credit_card: 'online', tamara: 'online', tabby: 'online',
      stripe: 'online', ziina: 'online', payment_link: 'link',
    };
    const lastPayment = (inv.payments || []).slice(-1)[0];
    // iter-243 — Show the remaining amount for partial invoices in the
    // hero; the stamp handles the classification.
    const amountToShow = inv.status === 'unpaid' ? (inv.total || 0)
                        : inv.status === 'partially_paid' ? (inv.paid_amount || 0)
                        : (inv.total || 0);
    return {
      receipt_no:     inv.invoice_no_display || `INV-${inv.invoice_no}`,
      request_no:     '',
      payer_name:     inv.customer?.name || '',
      payer_phone:    inv.customer?.phone || '',
      payer_email:    inv.customer?.email || '',
      amount_aed:     amountToShow,
      currency:       'AED',
      // iter-249 — Immutable TRN snapshot taken at invoice creation time.
      company_trn:    inv.company_trn || '',
      service_name_ar:     first.service_type_ar || first.service_type_en || '—',
      service_name_en:     first.service_type_en || first.service_type_ar || '—',
      sub_service_name_ar: joinedAr,
      sub_service_name_en: joinedEn,
      created_at:     inv.created_at,
      paid_at:        lastPayment?.created_at || inv.created_at,
      payment_method: methodMap[inv.payment_method] || 'bank_transfer',
      status:         inv.status || 'unpaid',
    };
  };

  const printAsReceipt = (inv) => {
    printReceipt(_invoiceToReceipt(inv), { language });
  };

  if (!platMeta) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <p className="text-white/60">{t('منصة غير معروفة', 'Unknown platform')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] relative">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full blur-[150px]"
             style={{ backgroundColor: `${platMeta.color}0d` }} />
      </div>

      <DashboardHeader showBackButton backPath={parentHubPath} />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية',      label_en: 'Finance',   path: '/dashboard/finance' },
            { label_ar: isAr ? platMeta.ar : platMeta.en, label_en: platMeta.en, path: parentHubPath },
            {
              // iter-260 — Match the parent hub card label (merchants
              // was renamed at that level; the inner page kept the old
              // generic "سجل المدفوعات" title which the user complained
              // about — align both).
              // iter-261 — agents platform has its own dedicated title.
              label_ar: platform === 'cloudgm_merchants'
                ? 'إيرادات منصة العملاء للفواتير و الخدمات'
                : platform === 'agents'
                ? 'سجل مدفوعات فواتير الوكلاء'
                : 'سجل المدفوعات',
              label_en: platform === 'cloudgm_merchants'
                ? 'Customer Platform Revenue — Invoices & Services'
                : platform === 'agents'
                ? 'Agent Invoice Payment Records'
                : 'Payment Records',
              path: null,
            },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div className={isAr ? 'text-right' : 'text-left'}>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: platMeta.color }} />
              <span className="text-white/50 text-sm">{isAr ? platMeta.ar : platMeta.en}</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              {platform === 'cloudgm_merchants'
                ? t('إيرادات منصة العملاء للفواتير و الخدمات', 'Customer Platform Revenue — Invoices & Services')
                : platform === 'agents'
                ? t('سجل مدفوعات فواتير الوكلاء', 'Agent Invoice Payment Records')
                : t('سجل المدفوعات', 'Payment Records')}
            </h1>
            <p className="text-white/50 text-sm">
              {platform === 'agents'
                ? t('سجل شامل بفواتير الوكلاء ومدفوعاتها — يشمل الدفع من المحفظة (خصم من العمولة)',
                   'Complete ledger of agent invoices — including wallet payments (commission deduction)')
                : t('سجل شامل بكل الفواتير والمدفوعات لهذه المنصة',
                   'Complete ledger of all invoices and payments for this platform')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={forceReconcile}
              variant="outline"
              className="border-white/10 bg-transparent text-white/80 hover:bg-white/5 h-11"
              data-testid="pcol-refresh-status-btn"
              title={t('تحديث حالة الفواتير من البوابات', 'Refresh status from gateways')}
            >
              <RefreshCw className="w-4 h-4 me-2" />
              {t('تحديث الحالة', 'Refresh')}
            </Button>
          </div>
        </div>

        {/* Stats strip */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard label={t('إجمالي الفواتير', 'Total invoices')} value={stats.count} />
          <StatCard label={t('المدفوعة', 'Paid')} value={stats.paid} color="#10b981" />
          <StatCard label={t('غير المدفوعة', 'Unpaid')} value={stats.unpaid} color="#f59e0b" />
          <StatCard
            label={t('إجمالي المُحصَّل', 'Collected')}
            value={stats.total_paid.toFixed(0)}
            color="#d4af37"
          />
        </section>

        {/* Records table */}
        <section className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
          <div className="flex items-center gap-3 p-4 border-b border-white/5">
            <h3 className="text-white font-bold flex-1">{t('الفواتير', 'Invoices')}</h3>
            <Button
              onClick={load}
              variant="outline"
              size="sm"
              className="border-white/10 bg-transparent text-white/80 hover:bg-white/5"
              data-testid="pcol-refresh"
            >
              <RefreshCw className={`w-4 h-4 me-1 ${loading ? 'animate-spin' : ''}`} />
              {t('تحديث', 'Refresh')}
            </Button>
          </div>

          {loading ? (
            <div className="p-16 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-white/40" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="p-12 text-center text-white/40 text-sm">
              {t('لا توجد فواتير بعد. ارجع إلى صفحة "منصة التجار (المالية)" واستخدم بطاقة "إنشاء فاتورة".',
                'No invoices yet. Return to "Merchant Platform (Finance)" and use the "Create Invoice" card.')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/50 text-xs border-b border-white/5">
                    <th className="text-start p-3 font-medium">#</th>
                    <th className="text-start p-3 font-medium">{t('العميل', 'Customer')}</th>
                    <th className="text-start p-3 font-medium">{t('الخدمات', 'Services')}</th>
                    <th className="text-start p-3 font-medium">{t('الإجمالي', 'Total')}</th>
                    <th className="text-start p-3 font-medium">{t('طريقة الدفع', 'Method')}</th>
                    <th className="text-start p-3 font-medium">{t('الحالة', 'Status')}</th>
                    <th className="text-start p-3 font-medium">{t('التاريخ', 'Date')}</th>
                    <th className="text-end p-3 font-medium">{t('إجراءات', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const stMeta = STATUS_META[inv.status] || STATUS_META.unpaid;
                    const SIcon = stMeta.icon;
                    const methodLabel = METHOD_LABEL[inv.payment_method] || { ar: inv.payment_method, en: inv.payment_method };
                    return (
                      <tr key={inv.id} className="border-b border-white/5 hover:bg-white/[0.02]"
                          data-testid={`pcol-invoice-row-${inv.id}`}>
                        <td className="p-3 text-white/90 font-mono text-xs">{inv.invoice_no_display || `INV-${inv.invoice_no}`}</td>
                        <td className="p-3">
                          <div className="text-white/90">{inv.customer?.name}</div>
                          {inv.customer?.email && <div className="text-white/40 text-xs">{inv.customer.email}</div>}
                        </td>
                        <td className="p-3 text-white/70 text-xs">
                          {inv.services?.length || 0} {t('خدمة', 'services')}
                          <div className="text-white/40 text-[11px] line-clamp-1">
                            {(inv.services || []).map(s => isAr ? s.service_type_ar : s.service_type_en).filter(Boolean).join(' • ')}
                          </div>
                        </td>
                        <td className="p-3 text-white/90 font-mono">
                          <DirhamPrice value={inv.total} decimals={2} size="sm" />
                          {inv.status === 'partially_paid' && (
                            <div className="text-[10px] mt-0.5">
                              <span className="text-emerald-400">{Number(inv.paid_amount || 0).toFixed(0)}</span>
                              <span className="text-white/30"> / </span>
                              <span className="text-amber-400">{Number(inv.remaining_amount || 0).toFixed(0)}</span>
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-white/80 text-xs">
                          {isAr ? methodLabel.ar : methodLabel.en}
                          {inv.gateway_provider && inv.payment_method !== inv.gateway_provider && (
                            <span className="text-white/40"> • {inv.gateway_provider}</span>
                          )}
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${stMeta.color}`}>
                            <SIcon className="w-3 h-3" />
                            {isAr ? stMeta.ar : stMeta.en}
                          </span>
                        </td>
                        <td className="p-3 text-white/60 text-xs">
                          {new Date(inv.created_at).toLocaleString(isAr ? 'ar-EG' : 'en-GB', {
                            year: '2-digit', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'
                          })}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1 justify-end">
                            {inv.checkout_url && (
                              <>
                                <button onClick={() => copyLink(inv.checkout_url)}
                                        className="p-1.5 rounded-md hover:bg-white/5 text-white/60"
                                        title={t('نسخ الرابط', 'Copy link')}
                                        data-testid={`pcol-copy-${inv.id}`}>
                                  <Copy className="w-4 h-4" />
                                </button>
                                <a href={inv.checkout_url} target="_blank" rel="noreferrer"
                                   className="p-1.5 rounded-md hover:bg-white/5 text-white/60"
                                   title={t('فتح', 'Open')}
                                   data-testid={`pcol-open-${inv.id}`}>
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              </>
                            )}
                            <button onClick={() => downloadPdf(inv)}
                                    className="p-1.5 rounded-md hover:bg-white/5 text-white/60"
                                    title={t('تحميل PDF', 'Download PDF')}
                                    data-testid={`pcol-pdf-${inv.id}`}>
                              <FileDown className="w-4 h-4" />
                            </button>
                            {(platform === 'cloudgm_merchants' || platform === 'cloudgm_customers') && (
                              <button onClick={() => printAsReceipt(inv)}
                                      className="p-1.5 rounded-md hover:bg-emerald-500/10 text-emerald-400"
                                      title={t('طباعة بتصميم إيصال CLOUDGM', 'Print with CLOUDGM receipt design')}
                                      data-testid={`pcol-print-receipt-${inv.id}`}>
                                <Printer className="w-4 h-4" />
                              </button>
                            )}
                            {inv.status === 'unpaid' && !inv.checkout_url && (
                              <button onClick={() => setLinkingInvoice(inv)}
                                      className="p-1.5 rounded-md flex items-center gap-1 text-xs font-medium bg-indigo-500/15 border border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/25 px-2"
                                      title={t('إنشاء رابط دفع للعميل', 'Generate payment link for customer')}
                                      data-testid={`pcol-gen-link-${inv.id}`}>
                                <Link2 className="w-4 h-4" />
                                <span className="hidden md:inline">
                                  {t('رابط دفع', 'Payment link')}
                                </span>
                              </button>
                            )}
                            {inv.status !== 'paid' && inv.status !== 'cancelled' && inv.status !== 'refunded' && (
                              <button onClick={() => setPayingInvoice(inv)}
                                      className={`p-1.5 rounded-md flex items-center gap-1 text-xs font-medium ${
                                        // iter-218n (Feb 2026) — Emphasise the "convert to paid" affordance
                                        // for unpaid invoices per user's spec.
                                        inv.status === 'unpaid'
                                          ? 'bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 px-2'
                                          : 'hover:bg-emerald-500/10 text-emerald-400/60 hover:text-emerald-400'
                                      }`}
                                      title={inv.status === 'unpaid'
                                        ? t('تحويل إلى مدفوع / مدفوع جزئي', 'Convert to paid / partially paid')
                                        : t('تسجيل دفعة', 'Add payment')}
                                      data-testid={`pcol-add-payment-${inv.id}`}>
                                <Wallet className="w-4 h-4" />
                                {inv.status === 'unpaid' && (
                                  <span className="hidden md:inline">
                                    {t('تحويل لمدفوع', 'Convert to paid')}
                                  </span>
                                )}
                              </button>
                            )}
                            {inv.status !== 'paid' && inv.status !== 'partially_paid' && inv.status !== 'refunded' && (inv.paid_amount || 0) === 0 && (
                              <button onClick={() => deleteInvoice(inv)}
                                      className="p-1.5 rounded-md hover:bg-red-500/10 text-red-400/60 hover:text-red-400"
                                      title={t('حذف', 'Delete')}
                                      data-testid={`pcol-delete-${inv.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                            {/* iter-260 — TEMPORARY (trial phase) force-delete
                                for PAID / partially paid receipts. Will be
                                removed once the platform goes live. */}
                            {(inv.status === 'paid' || inv.status === 'partially_paid') && (
                              <button onClick={() => deleteInvoice(inv, { force: true })}
                                      className="p-1.5 rounded-md bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300"
                                      title={t('حذف تجريبي (فترة اختبار)', 'Trial-mode force delete')}
                                      data-testid={`pcol-force-delete-${inv.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      {/* Add payment modal */}
      <AnimatePresence>
        {payingInvoice && (
          <AddPaymentModal
            invoice={payingInvoice}
            onClose={() => setPayingInvoice(null)}
            onDone={() => { setPayingInvoice(null); load(); }}
          />
        )}
      </AnimatePresence>

      {/* iter-220 — Convert unpaid invoice to payment link */}
      {linkingInvoice && (
        <GeneratePaymentLinkModal
          invoice={linkingInvoice}
          onClose={() => setLinkingInvoice(null)}
          onDone={() => { setLinkingInvoice(null); load(); }}
          isAr={isAr}
          t={t}
        />
      )}
    </div>
  );
}


function StatCard({ label, value, color = '#94a3b8' }) {
  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-white/60 text-sm font-medium">{label}</span>
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
    </div>
  );
}

// iter-220 (Feb 2026) — Modal for converting an unpaid manual invoice into
// a customer-facing payment link (Stripe / Tabby / Tamara / Ziina). Once
// the customer pays, the reconcile-pending job flips the invoice to
// `paid` automatically on the next list refresh.
function GeneratePaymentLinkModal({ invoice, onClose, onDone, isAr, t }) {
  const [provider, setProvider] = useState('stripe');
  const [submitting, setSubmitting] = useState(false);

  const providers = [
    { key: 'stripe',  ar: 'سترايب (بطاقة)', en: 'Stripe (Card)',      color: '#635BFF' },
    { key: 'tabby',   ar: 'تابي (تقسيط)',    en: 'Tabby (Installment)', color: '#3ECBB2' },
    { key: 'tamara',  ar: 'تمارا (تقسيط)',   en: 'Tamara (Installment)', color: '#FFB800' },
    { key: 'ziina',   ar: 'زيّنة (محفظة)',    en: 'Ziina (Wallet)',      color: '#7B61FF' },
  ];

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/invoices/${invoice.id}/generate-payment-link`,
        { provider },
        { headers: getAuthHeaders() },
      );
      const url = r.data?.checkout_url;
      toast.success(t('✓ تم إنشاء الرابط', '✓ Link created'));
      if (url) {
        navigator.clipboard.writeText(url).then(
          () => toast.success(t('تم نسخ الرابط', 'Link copied to clipboard')),
          () => {},
        );
      }
      onDone();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      let msg;
      if (typeof raw === 'string') msg = raw;
      else if (Array.isArray(raw)) msg = raw.map(x => x?.msg || JSON.stringify(x)).join(' · ');
      else msg = t('فشل الإنشاء', 'Create failed');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[100] p-4 flex items-start sm:items-center justify-center overflow-y-auto" onClick={onClose}>
      <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] w-full max-w-md my-6 sm:my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#2a2a4e]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
              <Link2 className="h-5 w-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">
                {t('تحويل إلى رابط دفع', 'Generate payment link')}
              </h2>
              <p className="text-xs text-[#71717A] font-mono" dir="ltr">
                {invoice.invoice_no_display || `INV-${invoice.invoice_no}`}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-[#0a0a0a] flex items-center justify-center hover:bg-red-500/20" data-testid="gen-link-close">
            <XCircle className="h-4 w-4 text-[#808080]" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-[#a0a0b8]">
            {t('اختر بوابة الدفع المطلوبة. سيتم إنشاء رابط وإرساله للعميل — الفاتورة تبقى غير مدفوعة حتى يتم الدفع الفعلي.',
               'Pick a payment gateway. A link will be generated and sent to the customer — the invoice stays "unpaid" until the payment actually clears.')}
          </p>
          <div className="text-xs text-[#71717A] p-3 rounded-lg bg-black/40 border border-white/5">
            <div className="flex justify-between">
              <span>{t('العميل', 'Customer')}</span>
              <span className="text-white">{invoice.customer?.name}</span>
            </div>
            <div className="flex justify-between mt-1">
              <span>{t('الإجمالي', 'Total')}</span>
              <span className="text-white font-bold">
                <DirhamPrice value={invoice.total} decimals={2} size="sm" />
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {providers.map((p) => (
              <button
                key={p.key}
                onClick={() => setProvider(p.key)}
                className={`p-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                  provider === p.key
                    ? 'bg-indigo-500/15 border-indigo-500 text-white'
                    : 'bg-[#0a0a0a] border-[#2a2a4e] text-[#71717A] hover:border-white/20'
                }`}
                data-testid={`gen-link-provider-${p.key}`}
              >
                <span className="block" style={{ color: provider === p.key ? p.color : undefined }}>
                  {isAr ? p.ar : p.en}
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 p-5 border-t border-[#2a2a4e]">
          <Button onClick={onClose} variant="outline" className="border-[#2a2a4e] text-white hover:bg-[#2a2a4e]">
            {t('إلغاء', 'Cancel')}
          </Button>
          <Button onClick={submit} disabled={submitting} className="bg-indigo-500 hover:bg-indigo-600 text-white gap-2" data-testid="gen-link-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            {t('إنشاء الرابط', 'Generate link')}
          </Button>
        </div>
      </div>
    </div>
  );
}

